/**
 * FitnessProgramsScreen - Lists fitness program templates by category
 * Created by neo on 21.01.26
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Container } from 'reactstrap';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useRootStore } from '../../../Store/useRootStore';
import { Theme } from '../../../Theme/Theme';
import {
  FitnessProgramTemplateCategory,
  FitnessProgramTemplateCategoryApi,
} from '../../../Model/Coach/Program/Template/FitnessProgramTemplateCategory';
import { FitnessProgramCategorySection } from './FitnessProgramCategorySection';
import { CoachProgram, CoachProgramApi } from '../../../Model/Coach/Program/CoachProgram';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const ScreenContainer = styled.div`
  animation: ${fadeIn} 0.5s ease-out;
  min-height: calc(100vh - 64px);
  background: ${Theme.Colors.background};
`;

const HeroSection = styled.div`
  background: white;
  padding: 32px 0 24px 0;
  border-bottom: 1px solid ${Theme.Colors.lighterGrey};
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const HeroTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 8px 0;
  line-height: 1.3;
  color: ${Theme.Colors.textColor};

  @media (max-width: 600px) {
    font-size: 1.5rem;
  }
`;

const AccentText = styled.span`
  background: linear-gradient(135deg, ${Theme.Colors.primary}, ${Theme.Colors.primaryDark});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const HeroSubtitle = styled.p`
  font-size: 1rem;
  color: ${Theme.Colors.charcoal};
  margin: 0;
  line-height: 1.5;
  max-width: 600px;

  @media (max-width: 600px) {
    font-size: 0.9rem;
  }
`;

const CategoriesSection = styled.div`
  padding: 32px 0 48px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: ${Theme.Colors.charcoal};
`;

export type FitnessProgramsScreenProps = {};

export const FitnessProgramsScreen: React.FC<FitnessProgramsScreenProps> = observer(() => {
  const { t } = useTrans();
  const { authentication } = useRootStore();

  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<FitnessProgramTemplateCategory[]>([]);
  const [activePrograms, setActivePrograms] = useState<CoachProgram[]>([]);

  useEffect(() => {
    Promise.all([FitnessProgramTemplateCategoryApi.find(), CoachProgramApi.currentlyActive()])
      .then(([cats, programs]) => {
        setCategories(cats);
        setActivePrograms(programs.filter((p) => p.type === 'program'));
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <ScreenContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            <AccentText>{t('fitnessPrograms.hero.accent', { defaultValue: 'Fitness' })}</AccentText>{' '}
            {t('fitnessPrograms.hero.title', { defaultValue: 'Programs' })}
          </HeroTitle>
          <HeroSubtitle>
            {t('fitnessPrograms.hero.subtitle', {
              defaultValue: 'Structured training programs designed to help you reach your fitness goals.',
            })}
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <CategoriesSection>
        <Container>
          {loading ? (
            <LoadingContainer>{t('common.loading', { defaultValue: 'Loading...' })}</LoadingContainer>
          ) : (
            categories.map((category) => (
              <FitnessProgramCategorySection key={category.id} category={category} activePrograms={activePrograms} />
            ))
          )}
        </Container>
      </CategoriesSection>
    </ScreenContainer>
  );
});
